import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useResolvedConfig } from "@/hooks/use-resolved-config";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiError } from "@/lib/api-error";
import { zonedDay } from "@/lib/zoned-day";

import { isReading, type Dish, type Menu, type MenuDetail } from "./menu-model";
import { isValid, scheduleBody, type MenuDraft, type ScheduleBody } from "./menu-fields";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** Today at the site as `YYYY-MM-DD`: menus start and end on the site's days, never the viewer's.
 * Null until the site's time zone has loaded. */
export function useSiteToday(siteId: string | null): string | null {
  const value = useResolvedConfig("site", siteId).data?.config.timezone?.value;
  return typeof value === "string" ? zonedDay(new Date(), value) : null;
}

/** One site's menus, or its archive. Polls every 5 s while any menu is being read. */
export function useSiteMenus(siteId: string, archived: boolean) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  return useQuery<Menu[]>({
    queryKey: ["menus", siteId, archived],
    queryFn: async () => {
      const params = new URLSearchParams({ site_id: siteId, archived: String(archived) });
      const res = await fetch(`${API_URL}/api/v1/menus/v2?${params}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
    refetchInterval: (query) =>
      query.state.data?.some((m) => isReading(m) || m.parts_reading.length > 0) ? 5000 : false,
  });
}

export interface ScheduleCheck {
  same_start: string | null;
}

/** What a menu with `draft`'s schedule would do at the site, asked once the draft is valid: the
 * menu with its type and start date, which it can replace (POS2-1456). Otherwise it is a menu of
 * its own (POS2-1469), and nothing gives way to it (POS2-1468). */
export function useScheduleCheck(siteId: string, draft: MenuDraft) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  const params = new URLSearchParams({ site_id: siteId });
  for (const [key, value] of Object.entries(scheduleBody(draft))) {
    if (value !== null) params.set(key, value);
  }
  return useQuery<ScheduleCheck>({
    // Not under "menus": a menu action refreshes those while its dialog is still open, and asked
    // again after a restore the check finds the restored menu itself, which the dialog's list of
    // the site's menus may not hold yet (POS2-1465).
    queryKey: ["upload-check", params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/menus/v2/upload-check?${params}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
    enabled: isValid(draft),
    placeholderData: keepPreviousData,
  });
}

/** One menu with its recipes as `siteId` sees them. Polls every 2 s while `poll` says so. */
export function useMenuDetail(menuId: string, siteId: string, poll: (menu: MenuDetail) => boolean) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  return useQuery<MenuDetail>({
    queryKey: ["menus", "detail", menuId, siteId],
    queryFn: async () => {
      const params = new URLSearchParams({ site_id: siteId });
      const res = await fetch(`${API_URL}/api/v1/menus/v2/${menuId}?${params}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
    refetchInterval: (query) => (query.state.data && poll(query.state.data) ? 2000 : false),
  });
}

/** Every dish on one site's menus, once each: All recipes. */
export function useSiteDishes(siteId: string) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  return useQuery<Dish[]>({
    queryKey: ["menus", siteId, "dishes"],
    queryFn: async () => {
      const params = new URLSearchParams({ site_id: siteId });
      const res = await fetch(`${API_URL}/api/v1/recipes/dishes?${params}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
  });
}

/** The site's currency; GBP until it loads, or when none is set anywhere up the chain. */
export function useSiteCurrency(siteId: string): string {
  const value = useResolvedConfig("site", siteId).data?.config.currency?.value;
  return typeof value === "string" ? value : "GBP";
}

/** The writes every Menus screen shares. Each refreshes the menus queries when it lands. */
export function useMenuActions() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["menus"] });

  async function patch(menuId: string, body: object): Promise<Menu> {
    const res = await fetch(`${API_URL}/api/v1/menus/v2/${menuId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await apiError(t, res);
    return (await res.json()).data;
  }

  async function send(path: string, method: "POST" | "DELETE"): Promise<void> {
    const res = await fetch(`${API_URL}/api/v1/menus/v2/${path}`, { method, headers });
    if (!res.ok) throw await apiError(t, res);
  }

  const setLive = useMutation({
    mutationFn: (v: { menuId: string; siteId: string; on: boolean }) =>
      patch(v.menuId, { live: { site_id: v.siteId, on: v.on } }),
    onSettled: refresh,
    onError: showApiError,
  });

  const saveSettings = useMutation({
    mutationFn: (v: { menuId: string; displayName: string; schedule: ScheduleBody }) =>
      patch(v.menuId, { display_name: v.displayName, schedule: v.schedule }),
    onSuccess: () => toast.success(t("menus.settings.saved")),
    onSettled: refresh,
    onError: showApiError,
  });

  const rename = useMutation({
    mutationFn: (v: { menuId: string; displayName: string }) =>
      patch(v.menuId, { display_name: v.displayName }),
    onSettled: refresh,
    onError: showApiError,
  });

  const restore = useMutation({
    mutationFn: (menuId: string) => send(`${menuId}/unarchive`, "POST"),
    onSettled: refresh,
    onError: showApiError,
  });

  /** The archive's "Set up and restore" for a menu from before POS2-1456: restored with this
   * name and schedule, its recipes placed on it, or its file read when it has none. */
  const setUpAndRestore = useMutation({
    mutationFn: async (v: {
      menuId: string;
      siteId: string;
      displayName: string;
      schedule: ScheduleBody;
    }) => {
      const res = await fetch(`${API_URL}/api/v1/menus/v2/${v.menuId}/set-up-and-restore`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          site_id: v.siteId,
          display_name: v.displayName,
          schedule: v.schedule,
        }),
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSettled: refresh,
    onError: showApiError,
  });

  const archive = useMutation({
    mutationFn: (menuId: string) => send(menuId, "DELETE"),
    onSettled: refresh,
    onError: showApiError,
  });

  /** Takes recipes off this menu only; the same dish on another menu is a separate row. */
  const removeRecipes = useMutation({
    mutationFn: async (v: { menuId: string; recipeIds: string[] }) => {
      const res = await fetch(`${API_URL}/api/v1/menus/v2/${v.menuId}/recipes`, {
        method: "DELETE",
        headers,
        body: JSON.stringify({ recipe_ids: v.recipeIds }),
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSettled: refresh,
    onError: showApiError,
  });

  /** Reads the menu's file again. Settles once the menus queries have refetched the new status. */
  const reread = useMutation({
    mutationFn: (menuId: string) => send(`${menuId}/extract`, "POST"),
    onSettled: refresh,
    onError: showApiError,
  });

  /** Archive at once with an Undo toast; a live menu's toast says Scrappy stopped matching it. */
  function archiveWithUndo(
    menuId: string,
    label: string,
    wasLive: boolean,
    onArchived?: () => void,
  ) {
    archive.mutate(menuId, {
      onSuccess: () => {
        onArchived?.();
        toast(
          wasLive
            ? t("menus.archived.toastLive", { name: label })
            : t("menus.archived.toast", { name: label }),
          { action: { label: t("menus.archived.undo"), onClick: () => restore.mutate(menuId) } },
        );
      },
    });
  }

  /** Takes a part, added before POS2-1469, back off its menu: Remove on the menu page. */
  const removePart = useMutation({
    mutationFn: (v: { menuId: string; partId: string }) =>
      send(`${v.menuId}/parts/${v.partId}`, "DELETE"),
    onSettled: refresh,
    onError: showApiError,
  });

  /** The menu's original file, saved under the name it was uploaded with (POS2-1470). */
  const download = useMutation({
    mutationFn: async (menu: Menu) => {
      const res = await fetch(`${API_URL}/api/v1/menus/v2/${menu.id}/download`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = menu.file_name;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: showApiError,
  });

  return {
    setLive,
    saveSettings,
    rename,
    restore,
    setUpAndRestore,
    archiveWithUndo,
    removeRecipes,
    reread,
    removePart,
    download,
  };
}
